<script setup lang="ts">
import { computed, ref } from 'vue';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Tag from 'primevue/tag';
import {
  useAiProfileValidation, useStringFunctions, useUtilities, useContacts, useAccounts, useToastNotifications,
} from '@apparatix/composables';
import { Content } from '@apparatix/types/AccountProfiles';
import InvalidProfileContent from './InvalidProfileContent.vue';
import { Typography } from '@apparatix/library/core';
import { Noir } from '@apparatix/theme/Noir';
import Menu from 'primevue/menu';
import { Address, ContactPayload } from '@apparatix/types/Contacts';
import { useAccountStore } from '@apparatix/stores/useAccountStore';
import { addressToCleanSnake } from '@apparatix/schemas/Addressable.ts';
import { useConfirm } from 'primevue/useconfirm';
import UsersIcon from '@primeicons/vue/users';
import ExclamationTriangleIcon from '@primeicons/vue/exclamation-triangle';
import EllipsisHIcon from '@primeicons/vue/ellipsis-h';

type Props = {
  contents: Content<'table'>;
  columns: string[];
  isContact?: boolean;
}

const props = defineProps<Props>();

const { validateTableSchema } = useAiProfileValidation();
const { arrayHasValues } = useUtilities();
const { capitalize } = useStringFunctions();
const { showSuccess } = useToastNotifications();

const isEmpty = computed(() => !arrayHasValues(props.contents));

const isValidSchema = computed(() => validateTableSchema(props.contents, props.columns));

const formatCategory = (category: string) => {
  if (category.includes('_')) {
    return category.split('_').map(c => capitalize(c)).join(' ');
  }

  return capitalize(category);
};

const getColumnWidth = (key: string) => key.toLowerCase() === 'description' ? '400px' : '200px';

const moreMenuRef = ref();
const selectedContact = ref();
const menuOptions = ref([
  {
    label: 'Create Contact',
    icon: UsersIcon,
    command: () => createContactClick(),
  },
]);

const onButtonClick = async (data: any, e: MouseEvent) => {
  const triggerElement = e.currentTarget as HTMLElement;
  selectedContact.value = data;
  moreMenuRef.value.toggle({ currentTarget: triggerElement } as unknown as MouseEvent);
};

function isAddress(a: unknown): a is Address {
  return !!a && typeof a === 'object' && 'address_line1' in (a as any);
}

const {
  createContact,
} = useContacts();
const { updateAccount } = useAccounts();
const accountStore = useAccountStore();
const confirm = useConfirm();

const isMeaningful = (v?: string | null) =>
  !!v && v.toString().trim() !== '' && v.toString().trim().toLowerCase() !== 'unknown';

const createContactClick = async () => {
  const fullName = selectedContact.value.name?.trim() || '';

  const existing = accountStore.accountAssociations.contacts?.some((c) => {
    const existingName = `${(c.firstName || '').trim()} ${(c.lastName || '').trim()}`.trim().toLowerCase();
    return existingName === fullName.toLowerCase();
  });

  if (existing) {
    confirm.require({
      group: 'aiCreateDuplicateConfirm',
      message: `Contact with name ${fullName} already exists. Are you sure you want to create this?`,
      header: 'Duplicate Contact',
      icon: ExclamationTriangleIcon,
      acceptLabel: 'OK',
      rejectLabel: 'Cancel',
      rejectProps: {
        label: 'Cancel',
        size: 'small',
        severity: 'secondary',
      },
      acceptProps: {
        label: 'Confirm',
        size: 'small',
      },
      accept: async () => {
        await createContactConfirmed();
      },
    });
    return;
  }

  await createContactConfirmed();
};

const createContactConfirmed = async () => {
  const payload: ContactPayload = {
    // eslint-disable-next-line camelcase
    full_name: selectedContact.value.name,
    // eslint-disable-next-line camelcase
    job_title: isMeaningful(selectedContact.value.seniority) ? selectedContact.value.seniority.trim() : undefined,
    // eslint-disable-next-line camelcase
    address_phone_numbers: isMeaningful(selectedContact.value.phone) ? [{ number: selectedContact.value.phone!.trim(), addressType: 'work' }] : undefined,
    // eslint-disable-next-line camelcase
    address_emails: isMeaningful(selectedContact.value.email) ? [{ email: selectedContact.value.email!.trim(), addressType: 'work' }] : undefined,
    // eslint-disable-next-line camelcase
    address_web_pages: isMeaningful(selectedContact.value.link) ? [{ url: selectedContact.value.link!.trim(), addressType: 'other' }] : undefined,
    // eslint-disable-next-line camelcase
    company_name: accountStore.accountDetails.name,
    addresses: (accountStore.accountDetails.addresses ?? [])
      .map(addr => addressToCleanSnake.parse(addr))
      .filter(isAddress)
      .filter(a => Object.keys(a).length > 0),
  };

  const result = await createContact(payload as ContactPayload);

  if (! result?.id) {
    throw new Error(`Error saving ${payload.full_name} as new Contact`);
  }

  const associatePayload = {
    data: {
      accounts: [
        {
          id: accountStore.accountDetails.id,
          contacts: accountStore.accountAssociations.contacts.map(contact => ({ id: contact.id, labelIds: contact.contactLabels?.map(label => label.contactLabelId) ?? []})),
        },
      ],
    },
  };

  let labelIds = [];
  let contactLabelIds = [];
  if (accountStore.accountAssociations.contacts?.length === 0) {
    labelIds = [1];
    contactLabelIds = [{ contactLabelId:1,contactLabelDisplayName:'Primary' }];
  }

  associatePayload.data.accounts[0].contacts.push({
    id: result.id,
    labelIds: labelIds,
  });

  await updateAccount(associatePayload);

  const contact = {
    id: result.id,
    firstName: result.contact.first_name,
    lastName: result.contact.last_name,
    contactLabels: contactLabelIds,
    companyName: result.contact.company_name,
    c2Name: result.contact.company_name,
    addressEmails: [],
    addressPhoneNumbers: [],
    jobTitle: result.contact.job_title,
  };


  if (payload.address_emails?.length) {
    contact.addressEmails = payload.address_emails;
  }

  if (payload.address_phone_numbers?.length) {
    contact.addressPhoneNumbers = payload.address_phone_numbers;
  }

  accountStore.accountAssociations.contacts.push(contact);

  showSuccess('Contact Created!');
};

</script>
<template>
  <DataTable
    v-if="isValidSchema"
    size="small"
    :value="props.contents"
    scrollable
  >
    <Column
      v-for="(key, idx) in props.columns"
      :key="idx"
      :field="key"
      :frozen="idx === 0"
      :header="key"
      :style="{minWidth: getColumnWidth(key)}"
    >
      <template #body="slotProps">
        <a
          v-if="key.toLowerCase() === 'link'"
          target="_blank"
          class="hover:underline text-[var(--p-indigo-600)]"
          :href="slotProps.data[key]"
        >{{ slotProps.data[key] }}</a>
        <Tag v-else-if="key.toLowerCase() === 'category'" :value="formatCategory(slotProps.data.category)" :dt="Noir.tag" />
        <Typography v-else variant="body">
          {{ slotProps.data[key] }}
        </Typography>
      </template>
    </Column>
    <Column
      v-if="props.isContact"
      frozen
      align-frozen="right"
    >
      <template #body="slotProps">
        <Button
          v-tooltip.left="'Options'"
          icon-only
          size="small"
          rounded
          class="icon-button"
          style="background:none; border:none; color:var(--primary-blue)"
          @click="(event) => onButtonClick(slotProps.data, event)"
        >
          <EllipsisHIcon size="1.5rem" color="var(--apx-dark-gray-400)" />
        </Button>
      </template>
    </Column>
  </DataTable>
  <Menu ref="moreMenuRef" :model="menuOptions" :popup="true" />
  <InvalidProfileContent v-if="!isValidSchema" :empty="isEmpty" />
</template>
<style scoped>
:deep(.p-datatable-column-title) {
  text-transform: capitalize;
}

:deep(.p-datatable-tbody > tr > td) {
  padding: 0.25rem 0.5rem 0.25rem 0.5rem;
}
</style>
